/**
 * Schedule / Slot schemas — provider availability.
 *
 * Enum arrays are tied to fhir-model types via `satisfies`.
 */
import { z } from 'zod';
import type { SlotStatus as FhirSlotStatus } from '@unify/fhir-model/resources/schedule';
import {
  getProviderWeeklyAvailabilityUiDisableReason,
  hasAtLeastOneWorkingDay,
  hasEmptySlotsInWeekly,
  hasEndNotAfterStartInWeekly,
  hasOverlappingSlotsInWeekly,
} from '@/features/calendar/providerAvailabilityValidation';
import type { ProviderWeeklyAvailability } from '@/features/calendar/providerAvailabilityTypes';

/** FHIR Slot.status values — synced with fhir-model. */
export const slotStatusValues = [
  'free',
  'busy',
  'busy-unavailable',
  'busy-tentative',
] as const satisfies readonly FhirSlotStatus[];

/** Days of week for weekly availability. */
export const dayOfWeekValues = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

/** Time window within a day. */
export const availabilityWindowSchema = z.object({
  start: z.string().regex(/^\d{2}:\d{2}$/, 'Must be HH:MM'),
  end: z.string().regex(/^\d{2}:\d{2}$/, 'Must be HH:MM'),
});

/**
 * Weekly availability pattern (stored as extension on Schedule).
 */
export const weeklyAvailabilitySchema = z.object({
  practitionerId: z.string().min(1, 'Provider is required'),
  locationId: z.string().optional(),
  days: z.record(
    z.enum(dayOfWeekValues),
    z.array(availabilityWindowSchema),
  ),
});

export type WeeklyAvailabilityInput = z.infer<typeof weeklyAvailabilitySchema>;

const timeLabel12hSchema = z
  .string()
  .regex(/^\d{1,2}:\d{2}\s*(AM|PM)$/i, 'Must be a 12-hour time')
  .refine((label) => {
    const match = label.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return false;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    return hour >= 1 && hour <= 12 && minute >= 0 && minute <= 59;
  }, 'Must be a valid 12-hour time');

const providerAvailabilitySlotUiSchema = z.object({
  start: z.union([timeLabel12hSchema, z.literal('')]),
  end: z.union([timeLabel12hSchema, z.literal('')]),
});

const providerDayAvailabilityUiSchema = z.object({
  enabled: z.boolean(),
  slots: z.array(providerAvailabilitySlotUiSchema),
});

/** UI weekly draft (12h labels) used by calendar Manage Availability + onboarding. */
export const providerWeeklyAvailabilityUiSchema = z
  .record(z.string(), providerDayAvailabilityUiSchema)
  .superRefine((weekly, ctx) => {
    const reason = getProviderWeeklyAvailabilityUiDisableReason(weekly as ProviderWeeklyAvailability);
    if (!reason) return;
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: reason });
  });

export type ProviderWeeklyAvailabilityUiInput = z.infer<typeof providerWeeklyAvailabilityUiSchema>;

export const providerWeeklyAvailabilityUiFieldLabels: Record<string, string> = {
  weekly: 'Weekly availability',
};

export function parseProviderWeeklyAvailabilityUi(
  weekly: ProviderWeeklyAvailability,
): ReturnType<typeof providerWeeklyAvailabilityUiSchema.safeParse> {
  return providerWeeklyAvailabilityUiSchema.safeParse(weekly);
}

/** Onboarding Availability section form values. */
export const availabilitySectionFormSchema = z.object({
  weekly: providerWeeklyAvailabilityUiSchema,
});

export type AvailabilitySectionFormValues = z.infer<typeof availabilitySectionFormSchema>;

export {
  hasAtLeastOneWorkingDay,
  hasEmptySlotsInWeekly,
  hasEndNotAfterStartInWeekly,
  hasOverlappingSlotsInWeekly,
  getProviderWeeklyAvailabilityUiDisableReason,
};

/**
 * Personal block / time-off Slot creation.
 */
export const personalBlockSchema = z.object({
  scheduleId: z.string().min(1, 'Schedule is required'),
  start: z.string().min(1, 'Start time is required'),
  end: z.string().min(1, 'End time is required'),
  status: z.enum(slotStatusValues).default('busy-unavailable'),
  comment: z.string().optional(),
});

export type PersonalBlockInput = z.infer<typeof personalBlockSchema>;
